import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import {
  LogOut,
  User,
  Moon,
  Sun,
  Menu,
  X,
  Clock,
  ChevronRight,
  ArrowRight,
} from "lucide-react";
import { apiService } from "../services/apiService";
import toast from "react-hot-toast";

const doesRecordBelongToOtherUser = (record, currentUser) => {
  if (!currentUser || !record) return false;
  const currentUserId = currentUser.id !== undefined && currentUser.id !== null ? String(currentUser.id).trim() : null;
  const currentEmail = currentUser.email ? String(currentUser.email).trim().toLowerCase() : null;
  const currentName = currentUser.name ? String(currentUser.name).trim().toLowerCase() : null;

  const recUserId = record.id_user_chp !== undefined && record.id_user_chp !== null ? String(record.id_user_chp).trim() : null;
  const recChpName = record.nama_chp ? String(record.nama_chp).trim().toLowerCase() : null;

  if (recUserId) {
    const matchesId = currentUserId && recUserId === currentUserId;
    const matchesEmail = currentEmail && recUserId.toLowerCase() === currentEmail;
    if (!matchesId && !matchesEmail) {
      return true;
    }
  } else if (recChpName) {
    const matchesName = currentName && recChpName === currentName;
    const matchesEmail = currentEmail && recChpName === currentEmail;
    if (!matchesName && !matchesEmail) {
      return true;
    }
  }

  return false;
};

const DashboardScreen = () => {
  const { user, logout } = useAuth();
  const { isDarkMode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [totalRecords, setTotalRecords] = useState(0);
  const [recentScans, setRecentScans] = useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    loadStats();
  }, [user]);

  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const rawRecords = await apiService.getCountingHistory(1000);
      const records = (rawRecords || []).filter(
        (record) => !doesRecordBelongToOtherUser(record, user)
      );

      if (records && records.length > 0) {
        // Calculate total records and store latest 3 scans
        setTotalRecords(records.length);
        setRecentScans(records.slice(0, 3));
      } else {
        setTotalRecords(0);
        setRecentScans([]);
      }
    } catch (error) {
      console.error("Error loading stats:", error);
      setTotalRecords(0);
      setRecentScans([]);
    } finally {
      setLoadingStats(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    try {
      return new Date(dateString).toLocaleDateString("id-ID", {
        day: "numeric",
        month: "short",
        year: "numeric",
      });
    } catch {
      return String(dateString);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return "";
    try {
      return new Date(dateString).toLocaleTimeString("id-ID", {
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "";
    }
  };

  const getCountTypeBadge = (countType) => {
    const badgeColors = {
      "MMS Blister":
        "bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800",
      "Capsule TTD":
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800",
      "TTD Capsule":
        "bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800",
      "MMS Pills":
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800",
      "MMS Botol":
        "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800",
    };

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${badgeColors[countType] || badgeColors["MMS Pills"]
          }`}
      >
        {countType || "MMS Pills"}
      </span>
    );
  };

  const handleLogout = () => {
    setShowLogoutConfirm(false);
    setIsMobileMenuOpen(false);
    logout();
    toast.success("Logged out successfully");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <div className="h-8 w-8 flex items-center justify-center mr-3">
                <img
                  src="/mms-logo.png"
                  alt="MMS Logo"
                  className="h-8 w-8 object-contain"
                />
              </div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                Pill Counting
              </h1>
              {process.env.REACT_APP_VERSION && (
                <span className="ml-2 px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300">
                  v{process.env.REACT_APP_VERSION}
                </span>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-4 space-x-4">
              <button
                onClick={toggleTheme}
                className="group relative rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 p-2 hover:scale-110 hover:shadow-lg"
                title={
                  isDarkMode ? "Switch to light mode" : "Switch to dark mode"
                }
              >
                {isDarkMode ? (
                  <Sun className="h-5 w-5 text-white" />
                ) : (
                  <Moon className="h-5 w-5 text-gray-600" />
                )}
              </button>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                <User className="h-4 w-4 mr-1" />
                {user?.name || user?.email}
              </div>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className="group flex items-center text-sm text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-all duration-300 hover:scale-105 hover:bg-red-50 dark:hover:bg-red-900/20 px-3 py-2 rounded-lg"
              >
                <LogOut className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>

            {/* Mobile Hamburger Button */}
            <div className="md:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="group p-2 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-all duration-300 hover:scale-110 hover:shadow-lg"
                aria-label="Toggle mobile menu"
              >
                {isMobileMenuOpen ? (
                  <X className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                ) : (
                  <Menu className="h-6 w-6 text-gray-600 dark:text-gray-300" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu Dropdown */}
          {isMobileMenuOpen && (
            <div className="md:hidden border-t border-gray-200 dark:border-gray-700 py-4">
              <div className="space-y-3">
                {/* Theme Toggle */}
                <button
                  onClick={() => {
                    toggleTheme();
                    setIsMobileMenuOpen(false);
                  }}
                  className="group flex items-center w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md"
                >
                  {isDarkMode ? (
                    <Sun className="h-5 w-5 text-yellow-500 mr-3" />
                  ) : (
                    <Moon className="h-5 w-5 text-gray-600 mr-3" />
                  )}
                  {isDarkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                </button>

                {/* User Info */}
                <div className="flex items-center px-3 py-2 text-sm text-gray-600 dark:text-gray-300">
                  <User className="h-5 w-5 mr-3" />
                  <span className="font-medium">
                    {user?.name || user?.email}
                  </span>
                </div>

                {/* Logout Button */}
                <button
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    setShowLogoutConfirm(true);
                  }}
                  className="group flex items-center w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-all duration-300 hover:scale-105 hover:shadow-md hover:text-red-600 dark:hover:text-red-400"
                >
                  <LogOut className="h-5 w-5 mr-3" />
                  Logout
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-28">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            Welcome back, {user?.name || "CHP"}!
          </h2>
          <p className="text-gray-600 dark:text-gray-300">
            Ready to help mothers with their pill counting today?
          </p>
        </div>

        {/* Latest Scanned Section */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
                <Clock className="h-5 w-5 text-blue-600 dark:text-blue-400 mr-2" />
                Latest Scanned
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Pemindaian terakhir yang berhasil tercatat
              </p>
            </div>
            {totalRecords > 0 && (
              <button
                onClick={() => navigate("/history")}
                className="group inline-flex items-center text-sm font-semibold text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/40 px-3 py-1.5 rounded-lg"
              >
                <span>View more ({totalRecords})</span>
                <ChevronRight className="h-4 w-4 ml-1 transition-transform group-hover:translate-x-0.5" />
              </button>
            )}
          </div>

          {loadingStats ? (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 text-center shadow-sm">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 dark:border-blue-400 mx-auto mb-2" />
              <p className="text-xs text-gray-500 dark:text-gray-400">Memuat riwayat pemindaian...</p>
            </div>
          ) : recentScans.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {recentScans.map((scan, idx) => (
                <div
                  key={idx}
                  onClick={() =>
                    navigate("/results", {
                      state: {
                        record: scan,
                        motherInfo: {
                          barcode_id: scan.barcode_id || scan.id_barcode,
                          mother_name: scan.nama_ibu || scan.mother_name,
                        },
                      },
                    })
                  }
                  className="group bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 hover:shadow-md transition-all duration-200 cursor-pointer flex flex-col justify-between"
                >
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-xs font-semibold text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/40 px-2 py-0.5 rounded border border-blue-100 dark:border-blue-800/50 truncate max-w-[55%]">
                        {scan.barcode_id || scan.id_barcode || "No Barcode"}
                      </span>
                      {getCountTypeBadge(scan.jenis_counting)}
                    </div>
                    <div className="mb-3">
                      <p className="text-xs text-gray-500 dark:text-gray-400">Nama Ibu</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">
                        {scan.nama_ibu || scan.mother_name || "-"}
                      </p>
                    </div>
                  </div>

                  <div className="pt-2.5 border-t border-gray-100 dark:border-gray-700/60 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                    <span className="font-bold text-gray-900 dark:text-white">
                      {scan.jumlah_pills ?? scan.pill_count ?? 0} {scan.jenis_counting?.toLowerCase().includes("capsule") ? "kapsul" : "tablet"}
                    </span>
                    <span className="text-gray-400 dark:text-gray-500">
                      {formatDate(scan.tanggal_submit || scan.timestamp)} {formatTime(scan.tanggal_submit || scan.timestamp)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white dark:bg-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-700 text-center shadow-sm">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Belum ada data pemindaian. Mulai pemindaian pertama Anda!
              </p>
            </div>
          )}
        </div>

        {/* Offline Status */}
        {/* <div className="mt-8">
          <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg
                  className="h-5 w-5 text-yellow-400 dark:text-yellow-500"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
                  Offline Mode Available
                </h3>
                <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
                  <p>
                    You can continue counting pills even when offline. Data will
                    sync when connection is restored.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div> */}
      </div>

      {/* Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-red-100 dark:bg-red-900/20 flex items-center justify-center">
                  <LogOut className="h-5 w-5 text-red-600 dark:text-red-400" />
                </div>
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Logout
                </h3>
              </div>
            </div>
            <div className="mb-6">
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Are you sure you want to logout? You will need to sign in again
                to continue using Smart MMS.
              </p>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowLogoutConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={handleLogout}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardScreen;
